import React from 'react';

// Import school images
import faithLogo from '../images/FAITH.jpg';
import sjavpaLogo from '../images/SJAVPA.jpg';
import blnhsLogo from '../images/BLNHS.jpg';
import blmcsLogo from '../images/BLMCS.jpg';

export default function EducationSection() {
  const educationData = [
    {
      date: '2023 - Present',
      logo: faithLogo,
      degree: 'Bachelor of Science in Computer Engineering (BSCpE)',
      school: 'First Asia Institute of Technology and Humanities (FAITH Colleges)',
      details: [
        { label: 'Level', value: 'College' },
        { label: 'Academic Focus', value: 'Software Development, Embedded Systems, IoT' },
        { label: 'Relevant Coursework', value: 'Data Structures & Algorithms, Computer Architecture, Advanced Web Development, Microprocessors' },
        { label: 'Specialization Interests', value: 'Full-Stack React Development, Machine Learning, Hardware-Software Integration' }
      ]
    },
    {
      date: '2021 - 2023',
      logo: sjavpaLogo,
      degree: 'Senior High School — Science, Technology, Engineering, and Mathematics (STEM)',
      school: 'St. John Academy of Visuals and Performing Arts (SJAVPA)',
      details: [
        { label: 'Level', value: 'Senior High School (Grade 11 – Grade 12)' },
        { label: 'Strand', value: 'STEM (Science, Technology, Engineering, and Mathematics)' }
      ]
    },
    {
      date: '2017 - 2021',
      logo: blnhsLogo,
      degree: 'Junior High School — Grade 7 to Grade 10',
      school: 'Bernardo Lirio National High School (BLNHS)',
      details: [
        { label: 'Level', value: 'Junior High School (Grade 7 – Grade 10)' }
      ]
    },
    {
      date: '2010 - 2017',
      logo: blmcsLogo,
      degree: 'Elementary — Kinder to Grade 6',
      school: 'Bernardo Lirio Memorial Central School (BLMCS)',
      details: [
        { label: 'Level', value: 'Elementary (Kinder – Grade 6)' }
      ]
    }
  ];

  return (
    <section id="education" className="section">
      <div className="section-content">
        <h2 className="section-title">Education</h2>
        <p style={{ marginBottom: '3rem', fontSize: '1.05rem', opacity: '0.9', maxWidth: '800px' }}>
          My academic background, degrees, and focus areas.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '3rem' }}>
          {educationData.map((edu, index) => (
            <div 
              key={index}
              className="glass-card"
              style={{ 
                padding: '0',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
            >
              {/* Left Panel - Institution Logo */}
              <div style={{ 
                flex: '0 0 340px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.06) 100%)',
                borderRight: '1px solid var(--glass-border)',
                padding: '3rem 2rem',
                position: 'relative'
              }}>
                {/* Vertical Accent Bar */}
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: '20%',
                  bottom: '20%',
                  width: '5px',
                  background: 'var(--primary)',
                  borderRadius: '0 4px 4px 0',
                  boxShadow: '0 0 20px var(--primary)'
                }}></div>

                {/* Institution Logo */}
                <div style={{
                  width: '260px',
                  height: '260px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  border: '2px solid var(--glass-border)',
                  padding: '1.75rem',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Subtle shine effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
                  }}></div>
                  
                  <img 
                    src={edu.logo} 
                    alt={`${edu.school} Logo`}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'contain',
                      filter: 'brightness(1.05) contrast(1.05)',
                      position: 'relative',
                      zIndex: 1
                    }} 
                  />
                </div>

                {/* Timeline Indicator */}
                <div style={{
                  marginTop: '1.5rem',
                  padding: '0.7rem 1.5rem',
                  background: 'linear-gradient(135deg, rgba(var(--primary-rgb, 99, 102, 241), 0.15) 0%, rgba(var(--primary-rgb, 99, 102, 241), 0.05) 100%)',
                  border: '1.5px solid var(--primary)',
                  borderRadius: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <span style={{
                    color: 'var(--primary)',
                    fontWeight: '700',
                    fontSize: '0.95rem',
                    letterSpacing: '0.5px'
                  }}>
                    {edu.date}
                  </span>
                </div>
              </div>

              {/* Right Panel - Education Details */}
              <div style={{ 
                flex: '1',
                padding: '3rem 3.5rem',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.15)',
                minHeight: '360px'
              }}>
                {/* Degree Information */}
                <div style={{ marginBottom: '2.5rem' }}>
                  <h3 style={{ 
                    fontSize: '1.75rem', 
                    marginBottom: '1rem',
                    color: 'var(--text)',
                    lineHeight: '1.3',
                    fontWeight: '700',
                    letterSpacing: '-0.5px'
                  }}>
                    {edu.degree}
                  </h3>
                  
                  {/* Institution Name */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '0.75rem',
                    padding: '1rem 0',
                    borderBottom: '2px solid var(--glass-border)'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '2px', flexShrink: 0 }}>
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                      <path d="M6 12v5c0 2 3 3 6 3s6-1 6-3v-5"/>
                    </svg>
                    <p style={{ 
                      color: 'var(--text)', 
                      fontWeight: '600',
                      fontSize: '1.1rem',
                      margin: 0,
                      lineHeight: '1.5',
                      opacity: '0.9'
                    }}>
                      {edu.school}
                    </p>
                  </div>
                </div>

                {/* Academic Details */}
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.5rem'
                }}>
                  {edu.details.map((detail, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'flex-start',
                        padding: '1rem 1.25rem',
                        background: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '10px',
                        border: '1px solid var(--glass-border)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                        e.currentTarget.style.borderColor = 'var(--primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                        e.currentTarget.style.borderColor = 'var(--glass-border)';
                      }}
                    >
                      {/* Icon Indicator */}
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: 'var(--primary)',
                        marginTop: '8px',
                        flexShrink: 0,
                        boxShadow: '0 0 10px var(--primary)'
                      }}></div>
                      
                      <div style={{ flex: 1 }}>
                        <span style={{ 
                          color: 'var(--primary)', 
                          fontWeight: '700',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '1.2px',
                          display: 'block',
                          marginBottom: '0.5rem'
                        }}>
                          {detail.label}
                        </span>
                        <span style={{ 
                          color: 'var(--muted)', 
                          fontSize: '1rem',
                          lineHeight: '1.7',
                          display: 'block'
                        }}>
                          {detail.value}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Responsive Styles */}
        <style>{`
          @media (max-width: 1024px) {
            .glass-card > div:first-child {
              flex: 0 0 100% !important;
              border-right: none !important;
              border-bottom: 1px solid var(--glass-border) !important;
              padding: 2.5rem 2rem !important;
            }
            .glass-card > div:last-child {
              padding: 2.5rem 2rem !important;
            }
            .glass-card {
              flex-direction: column !important;
            }
          }
        `}</style>

      </div>
    </section>
  );
}
